/**
 * Copyright © 2026 Adnuntius AS.
 *
 * Filters known error noise, then reports through AdnLogService.
 */
import {Inject, Injectable} from '@angular/core';
import * as _ from 'lodash';

import {AuthService} from '../../components/auth/auth.service';
import {AdnLogService, omitSensitiveRollbarFields} from '../../components/util/adn-log.service';
import {ROLLBAR_IGNORED_MESSAGES} from '../../components/util/adn-rollbar.service';
import {adsBlocked} from './ads-blocked';
import {WINDOW} from '../angular-platform-providers';

const ignoreMessages = ROLLBAR_IGNORED_MESSAGES;
const ignoreStatusCodes = [403, 401, 422];
function attemptObject(exception: any): any {
  if (_.isString(exception)) {
    try {
      const text = exception.replace('Possibly unhandled rejection: ', '');
      return JSON.parse(text);
    } catch (e) {
      return exception;
    }
  }
  return exception;
}

function buildRollbarError(exception: any, exceptionObject: any, window: any): any {
  let errLike: any = null;
  if (exception && (exception.message || exception.stack)) {
    errLike = exception;
  } else if (exceptionObject && (exceptionObject.message || exceptionObject.stack)) {
    errLike = exceptionObject;
  }

  if (!errLike) {
    return null;
  }
  if (exception instanceof window.Error || exception instanceof window.DOMException) {
    return exception;
  }

  const rollbarError = new window.Error(errLike.message || 'Unknown exception');
  if (errLike.name) {
    rollbarError.name = errLike.name;
  }
  if (errLike.stack) {
    rollbarError.stack = errLike.stack;
  }
  return rollbarError;
}

@Injectable({
  providedIn: 'root'
})
export class ExceptionHandlerService {
  constructor(
    private readonly sessionAuth: AuthService,
    private readonly adnLog: AdnLogService,
    @Inject(WINDOW) private readonly window: any
  ) {
  }

  handle(exception: any, cause?: any): void {
    const errorObj: any = {};
    if (_.isObject(exception) && (exception as any).message && (exception as any).stack) {
      // need to do this because _.cloneDeep doesn't copy the stuff!
      errorObj.message = (exception as any).message;
      errorObj.stack = (exception as any).stack;
    }
    const exceptionObject = _.isString(exception) ? attemptObject(exception) : errorObj.message ? errorObj : _.cloneDeep(exception);
    const statusCode = _.get(exceptionObject, ['status']);
    if (ignoreStatusCodes.indexOf(statusCode) > -1) {
      // don't need to report on a bunch of status codes.
      return;
    }
    if (statusCode === 404 || _.get(exceptionObject, ['detail', 'status']) === 404) {
      return;
    }
    const rejectionType = _.get(exceptionObject, 'type');
    if (rejectionType === 2 || rejectionType === 3 || rejectionType === 5) {
      return;
    }
    const rejectionMessage = _.get(exceptionObject, 'message');
    if (_.isString(rejectionMessage) && rejectionMessage.indexOf('superseded') > -1) {
      return;
    }
    if (statusCode === -1 && adsBlocked()) {
      // this failure is because of ad blockers. Can ignore.
      return;
    }
    if (statusCode === -1 && (!this.sessionAuth.isValidSession() || this.sessionAuth.getSession().isExpired())) {
      // happens when API is unreachable or when token refresh can't happen and edge cases like that
      return;
    }
    if (statusCode === -1) {
      this.adnLog.rollbar('error', '-1 status info', this.sessionAuth.getSession());
    }
    let exc = _.cloneDeep(_.get(exceptionObject, ['data', 'apiText']) || exception.message || (_.isObject(exceptionObject) && !_.isEmpty(exceptionObject) ? exceptionObject : exception));
    if (exception instanceof this.window.DOMException) {
      // this is a really weird exception that is not really an exception
      exc = {
        name: exception.name,
        message: exception.message,
        stack: exception.stack
      };
    }
    exc = omitSensitiveRollbarFields(exc);
    if (ignoreMessages.indexOf(exc) > -1) {
      return;
    }

    const message = _.isString(exc) ? exc : JSON.stringify(exc);
    this.adnLog.errorLocal(exception, cause);
    this.adnLog.rollbar('error', message, buildRollbarError(exception, exceptionObject, this.window), cause ? {cause: cause} : null);
  }
}
